import { MultipleMatchingConflict } from "@common/dto/financialAccount/mergeFinancialAccounts/multiple-matching-conflict.dto";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import classNames from "classnames";
import gridTableStyles from "@design-system/Tables/GridTable/GridTableBase.module.scss";
import { TransactionToMerge } from "./TransactionToMerge";
import styles from "./MultipleMatchingConflicts.module.scss";

interface Props {
    conflict: MultipleMatchingConflict;
    resolutions: Array<{
        fromDestination: number;
        toKeep: number;
    }>;
    onResolve: (
        resolutions: Array<{
            fromDestination: number;
            toKeep: number;
        }>,
    ) => void;
}

export const MultipleMatchingConflictResolving: React.FC<Props> = ({
    conflict,
    resolutions,
    onResolve,
}) => {
    const destinationValue = String(conflict.transactionFromDestination.id);
    const selectedResolution = resolutions.find(
        (resolution) =>
            resolution.fromDestination ===
            conflict.transactionFromDestination.id,
    );
    const selectedTransactionValue = selectedResolution
        ? String(selectedResolution.toKeep)
        : "";

    const handleSelectTransaction = (
        fromDestination: number,
        toKeep: number,
    ) => {
        const isAlreadySelected = resolutions.find(
            (resolution) => resolution.fromDestination === fromDestination,
        );

        if (isAlreadySelected && isAlreadySelected.toKeep !== toKeep) {
            return onResolve([
                ...resolutions.filter(
                    (resolution) =>
                        resolution.fromDestination !== fromDestination,
                ),
                { fromDestination, toKeep },
            ]);
        }

        onResolve([...resolutions, { fromDestination, toKeep }]);
    };

    return (
        <div
            className={classNames(gridTableStyles.gridTable, styles.table)}
            key={conflict.transactionFromDestination.id}
        >
            <div className={gridTableStyles.headerRow}>
                <div className={styles.headerCell}>In destination</div>
            </div>
            <TransactionToMerge
                transaction={conflict.transactionFromDestination}
                prepend={
                    <RadioGroup
                        name={`destination-${destinationValue}`}
                        aria-label="In destination"
                        value={destinationValue}
                        disabled
                    >
                        <Radio value={destinationValue} />
                    </RadioGroup>
                }
            />
            <div className={gridTableStyles.headerRow}>
                <div className={styles.headerCell}>To merge</div>
            </div>
            <RadioGroup
                name={`matching-${destinationValue}`}
                aria-label="Transaction to merge"
                value={selectedTransactionValue}
                onValueChange={(value) => {
                    const transaction = conflict.transactionsToMerge.find(
                        (candidate) => String(candidate.id) === value,
                    );
                    if (transaction) {
                        handleSelectTransaction(
                            conflict.transactionFromDestination.id,
                            transaction.id,
                        );
                    }
                }}
                className={styles.radioGroup}
            >
                {conflict.transactionsToMerge.map((transaction) => (
                    <TransactionToMerge
                        transaction={transaction}
                        key={transaction.id}
                        prepend={
                            <div>
                                <Radio
                                    value={String(transaction.id)}
                                    disabled={resolutions.some(
                                        (resolution) =>
                                            resolution.toKeep ===
                                            transaction.id,
                                    )}
                                />
                            </div>
                        }
                    />
                ))}
            </RadioGroup>
        </div>
    );
};
